import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { authService } from '@/services/auth.service';
import { useServiceStatusStore } from '@/store/serviceStatusStore';
import {
  getAccountsBaseUrl,
  getCollegeBaseUrl,
  getCandidatesBaseUrl,
  getAdminBaseUrl,
  getNotificationBaseUrl,
  getEmployerBaseUrl,
} from './endpoints';

// API Response types - Match Flutter response structure
export interface ApiResponse<T> {
  statusCode: number;
  message: string;
  data: T;
}

export interface ApiError {
  message: string;
  statusCode: number;
  errors?: Record<string, string[]>;
}

// Refresh token response type
interface RefreshTokenResponse {
  statusCode: number;
  message: string;
  data: {
    access_token: string;
    expires_in: number;
    token_type: string;
    message: string | null;
    refresh_token: string;
  };
  error: string;
}

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Function to refresh the access token
const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = authService.getRefreshToken();
  if (!refreshToken) {
    return null;
  }

  try {
    const response = await axios.post<RefreshTokenResponse>(
      `${getAccountsBaseUrl()}/api/Account/ReLoginByRefreshToken`,
      { refreshToken },
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );

    if (response.data?.statusCode === 200 && response.data?.data?.access_token) {
      const { access_token, refresh_token } = response.data.data;
      // Save the new tokens
      await authService.saveLoginData(access_token, refresh_token);
      return access_token;
    }

    return null;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return null;
  }
};

// Create axios instances for each service
const createApiClient = (getBaseUrl: () => string): AxiosInstance => {
  const client = axios.create({
    baseURL: getBaseUrl(),
    timeout: 60000, // 1 minute timeout like Flutter
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - Add auth token
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Skip adding Bearer token if Authorization header is already set (e.g., secure token)
      if (config.headers.Authorization) {
        return config;
      }
      const token = authService.getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - Handle errors
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiError>) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Show the Service Unavailable game on 502/503/504 and on network failures.
      const status = error.response?.status;
      const isGatewayError = status === 502 || status === 503;
      const isNetworkError =
        !error.response &&
        (error.code === 'ERR_NETWORK' ||
          error.code === 'ECONNABORTED' ||
          error.message === 'Network Error');

      if (isGatewayError || isNetworkError) {
        useServiceStatusStore.getState().setServiceUnavailable(true);
        return Promise.reject(error);
      }

      // List of public endpoints that don't require authentication
      // Also includes endpoints that should not trigger redirect on failure
      const publicEndpoints = [
        '/api/College/IsCollegeNameExits',
        '/api/College/IsMobileAndEmailExits',
        '/api/College/RegisterCollege',
        '/api/Account/SendOTP',
        '/api/Account/VerifyOTP',
        '/api/Account/Login',
        '/api/Account/ReLoginByRefreshToken',
        '/api/Admin/GetAllUniversities',
        '/api/Admin/GetAllCollegesInUniversity',
        '/api/Notification/SaveDeviceToken',
        '/api/College/GetCollegeDetailsByUId',
        '/api/Admin/GetInvitedCollegeByUId',
      ];

      const isPublicEndpoint = publicEndpoints.some((endpoint) =>
        originalRequest.url?.includes(endpoint)
      );

      // Handle 401 Unauthorized (skip for public endpoints)
      if (error.response?.status === 401 && !isPublicEndpoint && !originalRequest._retry) {
        if (isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              return client(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newToken = await refreshAccessToken();

          if (newToken) {
            // Token refreshed successfully
            processQueue(null, newToken);
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            return client(originalRequest);
          } else {
            // Refresh failed, clear auth and show session expired popup
            processQueue(error, null);
            await authService.clearAuthData();
            window.dispatchEvent(new CustomEvent('session-expired'));
            return Promise.reject(error);
          }
        } catch (refreshError) {
          processQueue(refreshError, null);
          await authService.clearAuthData();
          window.dispatchEvent(new CustomEvent('session-expired'));
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Format error response
      const apiError: ApiError = {
        message: error.response?.data?.message || error.message || 'An error occurred',
        statusCode: error.response?.status || 500,
        errors: error.response?.data?.errors,
      };

      return Promise.reject(apiError);
    }
  );

  return client;
};

// Service-specific API clients
export const accountsApi = createApiClient(getAccountsBaseUrl);
export const collegeApi = createApiClient(getCollegeBaseUrl);
export const candidatesApi = createApiClient(getCandidatesBaseUrl);
export const adminApi = createApiClient(getAdminBaseUrl);
export const notificationApi = createApiClient(getNotificationBaseUrl);
export const employerApi = createApiClient(getEmployerBaseUrl);

// Helper functions for common HTTP methods
export const api = {
  // Account service methods
  account: {
    get: <T>(url: string, params?: Record<string, unknown>) =>
      accountsApi.get<ApiResponse<T>>(url, { params }).then((res) => res.data),
    post: <T>(url: string, data?: unknown) =>
      accountsApi.post<ApiResponse<T>>(url, data).then((res) => res.data),
  },

  // College service methods
  college: {
    get: <T>(url: string, params?: Record<string, unknown>) =>
      collegeApi.get<ApiResponse<T>>(url, { params }).then((res) => res.data),
    post: <T>(url: string, data?: unknown) =>
      collegeApi.post<ApiResponse<T>>(url, data).then((res) => res.data),
    put: <T>(url: string, data?: unknown) =>
      collegeApi.put<ApiResponse<T>>(url, data).then((res) => res.data),
    patch: <T>(url: string, data?: unknown) =>
      collegeApi.patch<ApiResponse<T>>(url, data).then((res) => res.data),
    delete: <T>(url: string) =>
      collegeApi.delete<ApiResponse<T>>(url).then((res) => res.data),
    upload: <T>(url: string, formData: FormData, onProgress?: (progress: number) => void) =>
      collegeApi
        .post<ApiResponse<T>>(url, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            if (onProgress && progressEvent.total) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              onProgress(progress);
            }
          },
        })
        .then((res) => res.data),
  },

  // Admin service methods
  admin: {
    get: <T>(url: string, params?: Record<string, unknown>) =>
      adminApi.get<ApiResponse<T>>(url, { params }).then((res) => res.data),
    post: <T>(url: string, data?: unknown) =>
      adminApi.post<ApiResponse<T>>(url, data).then((res) => res.data),
  },

  // Candidates service methods
  candidates: {
    get: <T>(url: string, params?: Record<string, unknown>) =>
      candidatesApi.get<ApiResponse<T>>(url, { params }).then((res) => res.data),
    post: <T>(url: string, data?: unknown) =>
      candidatesApi.post<ApiResponse<T>>(url, data).then((res) => res.data),
    put: <T>(url: string, data?: unknown) =>
      candidatesApi.put<ApiResponse<T>>(url, data).then((res) => res.data),
    downloadBlob: (url: string, data?: unknown) =>
      candidatesApi.post(url, data, { responseType: 'blob' }).then((res) => res.data),
  },

  // Notification service methods
  notification: {
    get: <T>(url: string, params?: Record<string, unknown>) =>
      notificationApi.get<ApiResponse<T>>(url, { params }).then((res) => res.data),
    post: <T>(url: string, data?: unknown) =>
      notificationApi.post<ApiResponse<T>>(url, data).then((res) => res.data),
  },

  // Employer service methods
  employer: {
    get: <T>(url: string, params?: Record<string, unknown>) =>
      employerApi.get<ApiResponse<T>>(url, { params }).then((res) => res.data),
    post: <T>(url: string, data?: unknown) =>
      employerApi.post<ApiResponse<T>>(url, data).then((res) => res.data),
  },
};


export default api;
